/**
 * Where the archives came from, in a form that can be sent to a Web Worker (Blobs and URLs are
 * structured-cloneable; ByteSource objects are not). The worker rebuilds the same mount.
 */
import { archiveNameOf, archivesInIso, archivesInTome, BlobSource, isInstallerTome, type MountOptions, mountArchives, type NamedSource, parsePatchTarget, type PatchTarget } from '@d2/formats';
import { HttpRangeSource } from '../boot/sources';

export type SourceDescriptor =
  | { kind: 'devdisc'; url: string; name: string; size: number }
  | { kind: 'blob'; name: string; blob: Blob };

export const sourceDescriptors: SourceDescriptor[] = [];

/** The version the page mounted the archives as (`?patch=`); the worker mounts the same way. */
export const mountSettings: { patch: PatchTarget } = { patch: '1.14d' };

/**
 * The version the address asks for: `?patch=1.09d` (or `109`) reads the copy as 1.09d,
 * `?patch=off` (`0`, `false`, `no`, `1.07`) the discs' own 1.07 data; otherwise 1.14d.
 */
export function patchFromAddress(search: string): PatchTarget {
  return parsePatchTarget(new URLSearchParams(search).get('patch'));
}

export async function mountDescriptors(list: SourceDescriptor[], opts: MountOptions = {}) {
  const found: NamedSource[] = [];
  for (const d of list) {
    if (d.kind === 'blob') {
      const n = archiveNameOf(d.name);
      if (n) found.push({ name: n, source: new BlobSource(d.blob), origin: 'browser storage' });
    } else {
      const src = new HttpRangeSource(d.url, d.size);
      if (/\.iso$/i.test(d.name)) found.push(...(await archivesInIso(src, d.name)));
      else if (isInstallerTome(d.name)) found.push(...(await archivesInTome(src, d.name)).sources);
      else {
        const n = archiveNameOf(d.name);
        if (n) found.push({ name: n, source: src, origin: d.name });
      }
    }
  }
  return mountArchives(found, opts);
}
